/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { ChoiceGroup, IChoiceGroupOption } from '@fluentui/react/lib/ChoiceGroup';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import { useLocalStore } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { CollapsedErrorMessage } from 'src/core/components/CollapsedErrorMessage';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { AzureBlobContainerStore } from 'src/core/stores/AzureBlobContainerStore';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { createLabelsFileNamesStore, LabelsFileNamesStore } from 'src/customText/core/stores/LabelsFileNamesStore';
import { ModalFooter } from 'src/customText/verticals/projectCreationModal/components/ModalFooter';
import {
    projectCreationContentLeftMargin,
    ProjectCreationSteps,
    ProjectInfoDto
} from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import styled from 'styled-components';

export type ProjectCreationChooseContainerProps = {
    onDismiss: () => void;
    onPrevious: () => void;
    projectDto: ProjectInfoDto;
    isExtractionProject: boolean;
    onSubmit: (storageAccountName: string, containerName: string, labelsFileName: string, isNewFile: boolean) => void;
};

const messages = defineMessages({
    title: { id: 'customText.chooseContainerScreen.title', defaultMessage: 'Choose dataset location' },
    labelsFileLabel: { id: 'customText.chooseContainerScreen.labelsFileLabel', defaultMessage: 'Labels documents' },
    subscriptionLabel: { id: 'customText.chooseContainerScreen.subscriptionLabel', defaultMessage: 'Subscription:' },
    storageAccountLabel: { id: 'customText.chooseContainerScreen.storageAccountLabel', defaultMessage: 'Storage account:' },
    emptyItemPlaceholder: { id: 'customText.chooseContainerScreen.emptyItemPlaceholder', defaultMessage: 'No items found.' },
    blobContainerLabel: { id: 'customText.chooseContainerScreen.blobContainerLabel', defaultMessage: 'Blob store container' },
    labelsFileChoicePlaceholder: {
        id: 'customText.chooseContainerScreen.labelsFileChoicePlaceholder',
        defaultMessage: 'Choose the labels document'
    },
    labelsFileSearchPlaceholder: {
        id: 'customText.chooseContainerScreen.labelsFileSearchPlaceholder',
        defaultMessage: 'Search for your labels document'
    },
    moreDetails: { id: 'customText.chooseContainerScreen.moreDetails', defaultMessage: 'Learn more about the required permissions.' },
    searchBoxPlaceholder: {
        id: 'customText.chooseContainerScreen.searchBoxPlaceholder',
        defaultMessage: `Type in the name of your labels document.`
    },
    learnMoreAboutResourceRoles: {
        id: 'customText.chooseContainerScreen.learnMoreAboutResourceRoles',
        defaultMessage: 'Learn more about required roles for Language resource.'
    },
    description: {
        id: 'customText.chooseContainerScreen.description',
        defaultMessage: 'Choose the Blob store container which contains the dataset you wish to use with this project.'
    },
    blobContainerTooltipText: {
        id: 'customText.chooseContainerScreen.blobContainerTooltipText',
        defaultMessage: `This is the container within your Azure storage account "{storageAccountName}"
that contains your dataset (text documents, labels document)`
    },
    blobContainerPlaceholder: {
        id: 'customText.chooseContainerScreen.blobContainerPlaceholder',
        defaultMessage: 'Choose a blob container'
    },
    labelsFileTooltipText: {
        id: 'customText.chooseContainerScreen.labelsFileTooltipText',
        defaultMessage: 'Choose a predefined labels document,'
    },
    refreshDescription: {
        id: 'customText.chooseContainerScreen.refreshDescription',
        defaultMessage: `If you've made recent changes to your azure storage accounts,
you can click refresh here to make Custom Text aware of the changes`
    },
    rolesAssignmentWarning: {
        id: 'customText.chooseContainerScreen.rolesAssignmentWarning',
        defaultMessage: `Note: The roles assignment might take several minutes to reflect in
the portal. Please refresh the dropdown to get the latest values.`
    },
    lackPermissionsError: {
        id: 'customText.chooseContainerScreen.lackPermissionsError',
        defaultMessage: `Cannot access this container, please make sure to add your azure
resource "{resourceName}" as "storage blob data owner" on the storage account "{storageAccountName}".`
    },
    disabledCORSError: {
        id: 'customText.chooseContainerScreen.disabledCORSError',
        defaultMessage: `The portal cannot access your storage account "{storageAccountName}",
please enable CORS policies on your storage account.`
    },
    createFile: {
        id: 'customText.chooseContainerScreen.createFile',
        defaultMessage: 'No, I need to label my documents as part of this project'
    },
    existingFile: {
        id: 'customText.chooseContainerScreen.existingFile',
        defaultMessage: 'Yes, my documents are already labeled and I have a correctly formatted JSON labels file'
    },
    taggedDocumentsQuestionClassification: {
        id: 'customText.chooseContainerScreen.taggedDocumentsQuestionClassification',
        defaultMessage: 'Are your documents already labeled with classes?'
    },
    taggedDocumentsQuestionExtraction: {
        id: 'customText.chooseContainerScreen.taggedDocumentsQuestionExtraction',
        defaultMessage: 'Are your documents already labeled with entities?'
    },
    taggedDocumentsDescriptionClassification: {
        id: 'customText.chooseContainerScreen.taggedDocumentsDescriptionClassification',
        defaultMessage: `To create a custom classification model, your documents need to be labeled with classes.
You can label documents after you create the project or import an existing labels file.`
    },
    taggedDocumentsDescriptionExtraction: {
        id: 'customText.chooseContainerScreen.taggedDocumentsDescriptionExtraction',
        defaultMessage: `To create a custom NER model, your documents need to be labeled with entities.
You can label documents after you create the project or import an existing labels file.`
    },
    taggedDocumentsDescription2: {
        id: 'customText.chooseContainerScreen.taggedDocumentsDescription2',
        defaultMessage: 'Labels files can either be a JSON file following'
    },
    taggedDocumentsDescription3: {
        id: 'customText.chooseContainerScreen.taggedDocumentsDescription3',
        defaultMessage: 'or a file following'
    },
    languageStudio: {
        id: 'customText.chooseContainerScreen.languageStudio',
        defaultMessage: 'Language Studio formatting'
    },
    aml: {
        id: 'customText.chooseContainerScreen.aml',
        defaultMessage: ' Azure Machine Learning formats'
    },
    loadContainersButtonAriaLabel: {
        id: 'customText.chooseContainerScreen.loadContainersButtonAriaLabel',
        defaultMessage: 'load Containers'
    }
});

const stepTheme = getScopedTheme('ProjectCreationConnectStorage');
const refreshButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({
    iconDisabled: { color: stepTheme.disableColor },
    rootPressed: { backgroundColor: 'transparent' },
    rootHovered: { backgroundColor: 'transparent' },
    rootDisabled: { backgroundColor: 'transparent' },
    root: {
        marginTop: '30px'
    }
});

const StyledDropdown = styled(LuisDropdown)<{ rootWidth: string }>(props => ({
    width: props.rootWidth
}));

export const ChooseContainerScreen = observer((props: ProjectCreationChooseContainerProps) => {
    const { onSubmit, onDismiss, onPrevious, projectDto, isExtractionProject } = props;
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();

    const [containerName, setContainerName] = React.useState(projectDto?.containerName);
    const [labelsFileName, setLabelsFileName] = React.useState(projectDto?.labelsLocation);

    const [isCreateFileEnabled, setIsCreateFileEnabled] = React.useState(projectDto?.isNewFile ?? true);

    const { subscriptionStoreHouse } = rootStore.azureStore;
    const { csAccountStore, subscriptionId } = rootStore.azureStore.session;
    const { accountTokens } = csAccountStore;
    const ownedStorageAccountName = csAccountStore.ownedStorageAccountInfo.name;
    const subscriptionName = subscriptionStoreHouse.findById(subscriptionId).name;

    const { isLargeOrGreater } = useResponsiveMode();
    const containersTracker = useProgressTracker();
    const labelsFilesNamesLocalStore = useLocalStore<LabelsFileNamesStore>(createLabelsFileNamesStore);
    const azureContainersStoreHouse = useLocalStore<ResourceStoreHouse<AzureBlobContainerStore>>(createResourceStoreHouse);
    const submitTracker = useProgressTracker({
        onComplete: (blobName: string) => {
            const fileName = labelsFileName || blobName;
            onSubmit(ownedStorageAccountName, containerName, fileName, isCreateFileEnabled);
        }
    });

    const loadContainers = () => {
        dispatcher.dispatch('getAzureContainersByStorageAccountName', {
            ...csAccountStore.ownedStorageAccountInfo,
            tracker: containersTracker,
            localStore: azureContainersStoreHouse
        });
    };

    const loadLabelsFile = () => {
        if (containerName) {
            dispatcher.dispatch('fetchCustomTextProjectAvailableLabelsFileNames', {
                containerName,
                localStore: labelsFilesNamesLocalStore,
                storageAccountName: ownedStorageAccountName
            });
        }
    };

    const handleQueryChange = (newQuery: string) => {
        if (containerName && newQuery) {
            dispatcher.dispatch('fetchCustomTextProjectAvailableLabelsFileNames', {
                containerName,
                localStore: labelsFilesNamesLocalStore,
                storageAccountName: ownedStorageAccountName,
                prefix: newQuery
            });
        }

        if (!newQuery) {
            labelsFilesNamesLocalStore.setIsSearchingLabelsFiles(false);
        }

        if (newQuery && !labelsFilesNamesLocalStore.isSearchingLabelsFiles) {
            labelsFilesNamesLocalStore.setIsSearchingLabelsFiles(true);
        }
    };

    React.useEffect(() => {
        if (ownedStorageAccountName && !accountTokens.error) {
            loadContainers();
        }
    }, [ownedStorageAccountName]);

    React.useEffect(() => {
        if (!isCreateFileEnabled && !labelsFilesNamesLocalStore.isSearchingLabelsFiles) {
            loadLabelsFile();
        }
    }, [containerName, isCreateFileEnabled, labelsFilesNamesLocalStore.isSearchingLabelsFiles]);

    const isModalValid = containerName && (labelsFileName || isCreateFileEnabled);
    const isContainerDropdownDisabled = containersTracker.isRunning || submitTracker.isRunning;
    const isModalLoading = labelsFilesNamesLocalStore.tracker.isRunning || submitTracker.isRunning || containersTracker.isRunning;
    const isLabelsFilesDropdownDisabled =
        !containerName || isContainerDropdownDisabled || labelsFilesNamesLocalStore.tracker.isRunning || submitTracker.isRunning;

    const containersOptions: IDropdownOption[] = azureContainersStoreHouse.items.map(c => ({ key: c.name, text: c.name }));
    const labelsFilesNamesOptions: IDropdownOption[] = labelsFilesNamesLocalStore.items.map(f => ({
        key: f.id,
        text: f.id.toString().replace('.json', '')
    }));

    const labelsFilesDropdownPlaceholder = localize(
        labelsFilesNamesLocalStore.canDisplayLabelFiles && !labelsFilesNamesLocalStore.isSearchingLabelsFiles
            ? messages.labelsFileChoicePlaceholder
            : messages.labelsFileSearchPlaceholder
    );
    const labelsFilesDropdownEmptyStatePlaceholder = localize(
        labelsFilesNamesLocalStore.canDisplayLabelFiles || labelsFilesNamesLocalStore.isSearchingLabelsFiles
            ? messages.emptyItemPlaceholder
            : messages.labelsFileSearchPlaceholder
    );
    const labelsFileSourceOptions: IChoiceGroupOption[] = [
        { key: 'new', text: localize(messages.createFile) },
        {
            key: 'existing',
            text: localize(messages.existingFile),
            onRenderLabel: (labelProps, defaultLabelRenderer) => (
                <Stack>
                    {defaultLabelRenderer(labelProps)}

                    {!isCreateFileEnabled && (
                        <StyledDropdown
                            rootWidth={isLargeOrGreater ? '300px' : '90%'}
                            selectedKey={labelsFileName}
                            options={labelsFilesNamesOptions}
                            handleQueryChange={handleQueryChange}
                            placeholder={labelsFilesDropdownPlaceholder}
                            ariaLabel={labelsFilesDropdownPlaceholder}
                            onChange={(_, o) => setLabelsFileName(o.key as string)}
                            isLoading={labelsFilesNamesLocalStore.tracker.isRunning}
                            disabled={isLabelsFilesDropdownDisabled || isCreateFileEnabled}
                            emptyStatePlaceholder={labelsFilesDropdownEmptyStatePlaceholder}
                            searchBoxPlaceholder={localize(messages.searchBoxPlaceholder)}
                            onRenderLabel={() =>
                                labelOnRender(localize(messages.labelsFileLabel), localize(messages.labelsFileTooltipText), false)
                            }
                        />
                    )}
                </Stack>
            )
        }
    ];

    const handleOnSubmit = () => onSubmit(ownedStorageAccountName, containerName, labelsFileName, isCreateFileEnabled);

    const labelOnRender = (labelText: string, tooltipContent?: string | React.ReactNode, isRequired: boolean = true) => (
        <Stack horizontal verticalAlign="center">
            <Label styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required={isRequired}>
                {labelText}
            </Label>
            {tooltipContent && <CalloutButton isDarkMode={false} content={tooltipContent} />}
        </Stack>
    );

    const LabeledText = ({ label, text }: { label: string; text: string }) => (
        <Stack horizontal tokens={{ childrenGap: 4 }}>
            <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{label}</LuisText>
            <LuisText>
                {' '}
                &nbsp;
                {text}
            </LuisText>
        </Stack>
    );

    const handleChoiceGroupChange = (key: string) => {
        if (key === 'new') {
            setIsCreateFileEnabled(true);
            setLabelsFileName('');

            return;
        }
        setIsCreateFileEnabled(false);
    };

    return (
        <Stack>
            <Stack
                styles={{ root: { maxHeight: '400px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `${isLargeOrGreater ? `0 0 0 ${projectCreationContentLeftMargin}` : '12px 20px'}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>{localize(messages.description)}</LuisText>
                </Stack>

                <Stack tokens={{ childrenGap: 20 }}>
                    <LabeledText label={localize(messages.subscriptionLabel)} text={subscriptionName} />
                    <LabeledText label={localize(messages.storageAccountLabel)} text={ownedStorageAccountName} />
                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                        <StyledDropdown
                            options={containersOptions}
                            selectedKey={containerName}
                            isLoading={containersTracker.isRunning}
                            disabled={isContainerDropdownDisabled}
                            rootWidth={isLargeOrGreater ? '300px' : '90%'}
                            ariaLabel={localize(messages.blobContainerLabel)}
                            onChange={(_, o) => setContainerName(o.key as string)}
                            placeholder={localize(messages.blobContainerPlaceholder)}
                            emptyStatePlaceholder={localize(messages.emptyItemPlaceholder)}
                            onRenderLabel={() =>
                                labelOnRender(
                                    localize(messages.blobContainerLabel),
                                    localize(messages.blobContainerTooltipText, { storageAccountName: ownedStorageAccountName })
                                )
                            }
                        />
                        <LuisTooltip content={localize(messages.refreshDescription)}>
                            <IconButton
                                onClick={loadContainers}
                                styles={refreshButtonStyles}
                                iconProps={{ iconName: 'Refresh', ariaLabel: 'Refresh' }}
                                disabled={isContainerDropdownDisabled}
                                ariaLabel={localize(messages.loadContainersButtonAriaLabel)}
                            />
                        </LuisTooltip>
                    </Stack>

                    <Stack tokens={{ childrenGap: 5 }}>
                        <LuisText fontWeight={600}>
                            {isExtractionProject
                                ? localize(messages.taggedDocumentsQuestionExtraction)
                                : localize(messages.taggedDocumentsQuestionClassification)}
                        </LuisText>
                        <LuisText>
                            {isExtractionProject
                                ? localize(messages.taggedDocumentsDescriptionExtraction)
                                : localize(messages.taggedDocumentsDescriptionClassification)}
                            &nbsp;
                            {localize(messages.taggedDocumentsDescription2)}
                            &nbsp;
                            <ExternalLink
                                href={`https://aka.ms/${isExtractionProject ? 'ct-data-labeling-format' : 'ctc-data-labeling-format'}`}>
                                {localize(messages.languageStudio)}
                            </ExternalLink>
                            &nbsp;
                            {localize(messages.taggedDocumentsDescription3)}
                            &nbsp;
                            <ExternalLink href={`https://aka.ms/aml-data-file-format`}>{localize(messages.aml)}</ExternalLink>
                        </LuisText>

                        <ChoiceGroup
                            options={labelsFileSourceOptions}
                            disabled={isLabelsFilesDropdownDisabled}
                            selectedKey={isCreateFileEnabled ? 'new' : 'existing'}
                            onChange={(_, o) => handleChoiceGroupChange(o.key as string)}
                        />
                    </Stack>

                    {accountTokens.error && (
                        <CollapsedErrorMessage error={accountTokens.error}>
                            <ExternalLink
                                href={`https://aka.ms/${
                                    isExtractionProject ? 'ct-before-get-started-permissions' : 'ctc-before-get-started-permissions'
                                }`}>
                                {localize(messages.learnMoreAboutResourceRoles)}
                            </ExternalLink>
                        </CollapsedErrorMessage>
                    )}
                    {containersTracker.isFailed && (
                        <CollapsedErrorMessage
                            error={containersTracker.error}
                            collapsedErrorMessage={
                                containersTracker.error?.name === LuisConstants.HttpResponseCodes.NotAuthorizedAction &&
                                localize(messages.lackPermissionsError, {
                                    resourceName: csAccountStore.name,
                                    storageAccountName: ownedStorageAccountName
                                })
                            }>
                            <br />
                            <ExternalLink
                                href={`https://aka.ms/${
                                    isExtractionProject ? 'ct-before-get-started-permissions' : 'ctc-before-get-started-permissions'
                                }`}>
                                {localize(messages.moreDetails)}
                            </ExternalLink>
                            <br />
                            <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>
                                {localize(messages.rolesAssignmentWarning)}
                            </LuisText>
                        </CollapsedErrorMessage>
                    )}
                    {labelsFilesNamesLocalStore.tracker.isFailed && (
                        <CollapsedErrorMessage
                            error={labelsFilesNamesLocalStore.tracker.error}
                            collapsedErrorMessage={
                                labelsFilesNamesLocalStore.tracker.error?.name === LuisConstants.HttpResponseCodes.ClientSideFailure &&
                                localize(messages.disabledCORSError, { storageAccountName: ownedStorageAccountName })
                            }
                        />
                    )}
                    {submitTracker.isFailed && (
                        <CollapsedErrorMessage
                            error={submitTracker.error}
                            collapsedErrorMessage={
                                submitTracker.error?.name === LuisConstants.HttpResponseCodes.NotAuthorizedAction &&
                                localize(messages.lackPermissionsError, {
                                    resourceName: csAccountStore.name,
                                    storageAccountName: ownedStorageAccountName
                                })
                            }
                        />
                    )}
                </Stack>
            </Stack>

            <ModalFooter
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                activeStep={ProjectCreationSteps.ChooseContainer}
                onNext={handleOnSubmit}
                isValid={!!isModalValid}
                isLoading={isModalLoading}
                isFooterDisabled={submitTracker.isRunning}
            />
        </Stack>
    );
});
